import React, { useContext, useEffect, useState } from "react";
import { Formik, Form, ErrorMessage } from "formik";
import * as Yup from "yup";
import {
  Button,
  Card,
  CardContent,
  Container,
  Grid,
  TextField,
  Divider,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import Header, { HeaderType } from "../../components/Header";
import {
  CreateAddress,
  DeleteAddress,
  EditAddress,
  GetAddress,
} from "../../services/api/address-service";
import { getAccessToken, useAuth } from "../../context/auth";
import type { IAddress } from "../../services/model/address";
import { useConfirm } from "@/components/dialog/confirm";
import BrandLoader from "@/branding/BrandLoader";
import { environment } from "@/environment/environment";
import GoogleAddress from "../../components/GoogleAddress/GoogleAddress";
import { BrandingContext } from "@/branding/BrandingProvider";
import DigiMap from "@/components/DigiMap/DigiMap";
type AddAddressProps = {
  style?: any;
  loading: boolean;
  isEmptyState: true;
};

const AddEditAddress: React.FC<AddAddressProps> = () => {
  const [canClose, setCanClose] = useState(false);
  const [addressFields, setAddressFields] = React.useState<IAddress>({
    id: null,
    googleAddress: "",
    linkedId: null,
    latitude: undefined,
    longitude: undefined,
  });
  let [loading, setLoading] = useState(false);
  const { authTokens } = useAuth();
  const token = getAccessToken(authTokens) ?? "";
  const navigate = useNavigate();
  const { tenant } = useParams<{ tenant: string }>();
  const branding = useContext(BrandingContext);
  const enableGoogleMaps =
    !!branding?.enableGoogleMaps &&
    String(environment.GoogleApiKey ?? "").trim().length > 0;

  const deleteRecord = () => {
    confirm({
      catchOnCancel: false,
      title: "Confirmation",
      description: "Are you sure you would like to delete this record?",
    }).then(() => runDelete());
  };

  const runDelete = async () => {
    try {
      setLoading(true);
      var viewAddressId = JSON.parse(
        window.localStorage.getItem("viewAddressId") || ""
      );
      const response = await DeleteAddress(token, viewAddressId);
      if (response) {
        setLoading(false);
        cancel();
      } else {
        setLoading(false);
        showDialog(
          "Address",
          "There seems to be a problem with your internet connection. Please check your connection and try again."
        );
      }
    } catch (err) {
      setLoading(false);
      showDialog(
        "Failure",
        "An exception occurred, please contact your administrator."
      );
      if (err instanceof Error) {
        console.error(err.message);
      }
    }
  };

  const cancel = () => {
    localStorage.removeItem("addressFormData");
    navigate(`/${tenant}/addresslist`, { replace: true });
  };

  const fetchProfile = async () => {
    try {
      setLoading(true);
      let storedValue = window.localStorage.getItem("viewAddressId");
      let viewAddressId: string;
      if (storedValue) {
        viewAddressId = JSON.parse(storedValue);
        const response = await GetAddress(token, viewAddressId);
        setAddressFields(response.data);
      } else {
        setAddressFields({
          id: null,
          googleAddress: "",
          latitude: undefined,
          longitude: undefined,
          linkedId: null,
        });
      }

      setLoading(false);
    } catch (err) {
      setLoading(false);
      showDialog(
        "Failure",
        "An exception occurred, please contact your administrator."
      );
      if (err instanceof Error) {
        console.error(err.message);
      }
    }
  };

  useEffect(() => {
    fetchProfile();
  }, []);

  const confirm = useConfirm();
  const showDialog = (title: string, message: string) => {
    confirm({
      catchOnCancel: false,
      title: title,
      description: message,
    }).then(() => {
      if (canClose === true) {
        cancel();
      }
    });
  };

  return (
    <div>
      <Formik
        initialValues={addressFields}
        enableReinitialize
        validationSchema={Yup.object({
          latitude: Yup.number().nullable().required("Latitude is required"),
          longitude: Yup.number().nullable().required("Longitude is required"),
        })}
        onSubmit={async (fields) => {
          try {
            setLoading(true);
            if (fields) {
              let response;

              if (addressFields.id == null) {
                (addressFields.id = "00000000-0000-0000-0000-000000000000"),
                  (addressFields.linkedId =
                    "00000000-0000-0000-0000-000000000000"),
                  (response = await CreateAddress(addressFields, token));
              } else {
                response = await EditAddress(addressFields, token);
              }

              setLoading(false);
              if (response) {
                showDialog("Address", response.message);
              } else {
                showDialog(
                  "Address",
                  "There seems to be a problem with your internet connection. Please check your connection and try again."
                );
              }
            }
          } catch (err) {
            setLoading(false);
            showDialog(
              "Failure",
              "An exception occurred, please contact your administrator."
            );
            if (err instanceof Error) {
              console.error(err.message);
            }
          }
        }}
      >
        {(props) => {
          const { touched, errors } = props;
          return (
            <>
              <Header type={HeaderType.AddEditAddress} />
              <Container maxWidth="sm">
                <Grid container spacing={2}>
                  <Card variant="outlined">
                    <CardContent>
                      <Form>
                        <Grid container spacing={1}>
                          {enableGoogleMaps ? (
                            <GoogleAddress
                              addressFields={addressFields}
                              setAddressFields={setAddressFields}
                              enableMap={true}
                            />
                          ) : (
                            <DigiMap
                              addressFields={addressFields}
                              setAddressFields={setAddressFields}
                              enableMap={true}
                            />
                          )}
                          <TextField
                            type="text"
                            label="Latitude"
                            value={addressFields?.latitude ?? ""}
                            id="latitude"
                            className={
                              errors.latitude && touched.latitude
                                ? " is-invalid"
                                : ""
                            }
                            required
                            disabled
                            fullWidth
                            variant="outlined"
                            error={
                              (errors.latitude && touched.latitude) === true
                            }
                          />
                          <ErrorMessage
                            name="latitude"
                            component="div"
                            className="invalid-feedback"
                          />

                          <TextField
                            type="text"
                            label="Longitude"
                            value={addressFields?.longitude ?? ""}
                            id="longitude"
                            className={
                              errors.longitude && touched.longitude
                                ? " is-invalid"
                                : ""
                            }
                            required
                            disabled
                            fullWidth
                            variant="outlined"
                            error={
                              (errors.longitude && touched.longitude) === true
                            }
                          />
                          <ErrorMessage
                            name="longitude"
                            component="div"
                            className="invalid-feedback"
                          />

                          <Divider sx={{ pb: 3 }} />
                          <Button
                            type="submit"
                            value="Save"
                            variant="contained"
                            color="primary"
                            fullWidth
                            size="large"
                            onClick={() => {
                              setCanClose(true);
                            }}
                          >
                            <strong>Save</strong>
                          </Button>
                          {addressFields.id && (
                            <Button
                              type="button"
                              value="Delete"
                              variant="contained"
                              color="secondary"
                              fullWidth
                              size="large"
                              onClick={deleteRecord}
                            >
                              <strong>Delete</strong>
                            </Button>
                          )}
                        </Grid>
                      </Form>
                    </CardContent>
                  </Card>
                </Grid>
              </Container>
            </>
          );
        }}
      </Formik>
      <BrandLoader open={loading} />
    </div>
  );
};

export default AddEditAddress;
